import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Command Center Drawer
 *
 * The core drill-down navigation pattern for the Command Center.
 * Slides in from the right with a scrim backdrop.
 * Supports breadcrumb navigation for multi-level drilling.
 *
 * Shared component - works in both TekStak (blue) and GAS (orange).
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, ChevronRight, ArrowLeft } from 'lucide-react';
// =============================================================================
// DRAWER CONTEXT
// =============================================================================
const DrawerContext = createContext(null);
export function useDrawer() {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error('useDrawer must be used within a DrawerProvider');
    }
    return context;
}
// =============================================================================
// DRAWER PROVIDER
// =============================================================================
export function DrawerProvider({ children }) {
    const [stack, setStack] = useState([]);
    const open = useCallback((content, options = {}) => {
        setStack(prev => [...prev, { content, ...options }]);
    }, []);
    const close = useCallback(() => {
        setStack([]);
    }, []);
    const back = useCallback(() => {
        setStack(prev => prev.slice(0, -1));
    }, []);
    const replace = useCallback((content, options = {}) => {
        setStack(prev => [...prev.slice(0, -1), { content, ...options }]);
    }, []);
    const isOpen = stack.length > 0;
    const current = stack[stack.length - 1];
    const canBack = stack.length > 1;
    const value = {
        isOpen,
        current,
        stack,
        canBack,
        open,
        close,
        back,
        replace,
    };
    return (_jsx(DrawerContext.Provider, { value: value, children: children }));
}
// =============================================================================
// DRAWER COMPONENT
// =============================================================================
export function Drawer({ width = 430 }) {
    const { isOpen, current, stack, canBack, close, back } = useDrawer();
    // Keyboard behavior (audit X-3): Escape steps BACK through the drill
    // stack (matching the visual breadcrumb model) and closes at the root.
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                if (stack.length > 1)
                    back();
                else
                    close();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, stack.length, back, close]);
    const crumbs = stack.map((item, index) => ({
        label: item.title || `Level ${index + 1}`,
        isLast: index === stack.length - 1,
    }));
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `cc-scrim ${isOpen ? 'open' : ''}`, onClick: close, style: { zIndex: 40 } }), _jsx("div", { className: `cc-drawer ${isOpen ? 'open' : ''}`, role: "dialog", "aria-modal": "true", "aria-label": current?.title || 'Details drawer', style: {
                    width: `${width}px`,
                    maxWidth: '90%',
                    zIndex: 41,
                }, children: isOpen && current && (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', height: '100%' }, children: [_jsxs("div", { style: {
                                flex: 'none',
                                padding: '16px 18px 12px',
                                borderBottom: '1px solid var(--cc-border)',
                            }, children: [_jsxs("div", { style: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        fontSize: 11,
                                        color: 'var(--cc-text-3)',
                                        fontFamily: "'JetBrains Mono', monospace",
                                        marginBottom: 10,
                                        flexWrap: 'wrap',
                                    }, children: [_jsx("span", { children: "Execution" }), crumbs.map((crumb, i) => (_jsxs(React.Fragment, { children: [_jsx(ChevronRight, { size: 12 }), _jsx("span", { style: {
                                                        color: crumb.isLast ? 'var(--cc-text-2)' : 'var(--cc-text-3)',
                                                        cursor: crumb.isLast ? 'default' : 'pointer',
                                                    }, children: crumb.label })] }, i)))] }), _jsxs("div", { style: { display: 'flex', alignItems: 'flex-start', gap: 10 }, children: [canBack && (_jsx("button", { onClick: back, style: {
                                                width: 30,
                                                height: 30,
                                                flexShrink: 0,
                                                borderRadius: 8,
                                                border: '1px solid var(--cc-border-strong)',
                                                background: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: 'var(--cc-text-2)',
                                            }, children: _jsx(ArrowLeft, { size: 14 }) })), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("div", { style: {
                                                        fontSize: 17,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.02em',
                                                        lineHeight: 1.2,
                                                        color: 'var(--cc-text)',
                                                    }, children: current.title }), current.subtitle && (_jsx("div", { style: {
                                                        fontSize: 12.5,
                                                        color: 'var(--cc-text-2)',
                                                        marginTop: 3,
                                                    }, children: current.subtitle }))] }), _jsx("button", { onClick: close, "aria-label": "Close drawer", style: {
                                                width: 30,
                                                height: 30,
                                                flexShrink: 0,
                                                borderRadius: 8,
                                                border: 'none',
                                                background: 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                color: 'var(--cc-text-2)',
                                            }, children: _jsx(X, { size: 15 }) })] })] }), _jsx("div", { className: "cc-scroll", style: {
                                flex: 1,
                                overflowY: 'auto',
                                padding: '6px 12px 18px',
                            }, children: current.content })] })) })] }));
}
// =============================================================================
// DRAWER CONTENT HELPERS
// =============================================================================
const variantStyles = {
    ok: { bg: 'var(--cc-ok-bg)', color: 'var(--cc-ok)' },
    warn: { bg: 'var(--cc-warn-bg)', color: 'var(--cc-warn)' },
    danger: { bg: 'var(--cc-danger-bg)', color: 'var(--cc-danger)' },
    accent: { bg: 'var(--cc-accent-tint-2)', color: 'var(--cc-accent-strong)' },
    neutral: { bg: 'var(--cc-neutral-bg)', color: 'var(--cc-neutral)' },
};
/**
 * Status banner for drawer content
 */
export function DrawerStatus({ variant = 'ok', icon: Icon, children }) {
    const style = variantStyles[variant] || variantStyles.ok;
    return (_jsxs("div", { style: {
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '11px 13px',
            borderRadius: 11,
            background: style.bg,
            color: style.color,
        }, children: [Icon && _jsx(Icon, { size: 16 }), _jsx("span", { style: { fontSize: 13, fontWeight: 700 }, children: children })] }));
}
/**
 * Section title for drawer content
 */
export function DrawerSection({ title, children }) {
    return (_jsxs("div", { children: [_jsx("div", { style: {
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    color: 'var(--cc-text-3)',
                    margin: '16px 0 9px',
                }, children: title }), children] }));
}
/**
 * Row item for drawer lists
 */
export function DrawerRow({ icon, mono, label, meta, right, rightVariant, onClick, drillable = true, }) {
    return (_jsxs("div", { onClick: onClick, className: "cc-hover-tint", style: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 11px',
            borderRadius: 10,
            cursor: onClick ? 'pointer' : 'default',
            border: '1px solid var(--cc-border)',
            background: 'var(--cc-elevated)',
            marginBottom: 6,
        }, children: [mono && (_jsx("span", { style: {
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: 'var(--cc-border-strong)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontWeight: 800,
                    color: 'var(--cc-text-2)',
                    fontFamily: "'JetBrains Mono', monospace",
                }, children: mono })), icon && !mono && (_jsx("span", { style: {
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 8,
                    background: 'var(--cc-accent-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cc-accent-strong)',
                }, children: icon })), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("div", { style: {
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: 'var(--cc-text)',
                        }, children: label }), meta && (_jsx("div", { style: {
                            fontSize: 11,
                            color: 'var(--cc-text-3)',
                            marginTop: 1,
                        }, children: meta }))] }), right && (_jsx("span", { className: "cc-pill", style: {
                    background: rightVariant ? variantStyles[rightVariant]?.bg : 'var(--cc-field)',
                    color: rightVariant ? variantStyles[rightVariant]?.color : 'var(--cc-text-2)',
                }, children: right })), drillable && onClick && (_jsx(ChevronRight, { size: 14, style: { color: 'var(--cc-text-3)' } }))] }));
}
/**
 * Action button for drawer
 */
export function DrawerAction({ icon: Icon, title, subtitle, onClick }) {
    return (_jsxs("div", { onClick: onClick, style: {
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '11px 13px',
            border: '1px solid var(--cc-border-strong)',
            borderRadius: 11,
            background: 'var(--cc-elevated)',
            cursor: 'pointer',
            marginBottom: 8,
            transition: 'border-color 0.14s ease, background 0.14s ease',
        }, onMouseOver: (e) => {
            e.currentTarget.style.borderColor = 'var(--cc-accent)';
            e.currentTarget.style.background = 'var(--cc-accent-tint-2)';
        }, onMouseOut: (e) => {
            e.currentTarget.style.borderColor = 'var(--cc-border-strong)';
            e.currentTarget.style.background = 'var(--cc-elevated)';
        }, children: [_jsx("span", { style: {
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 8,
                    background: 'var(--cc-accent-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cc-accent-strong)',
                }, children: Icon && _jsx(Icon, { size: 14 }) }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 700, color: 'var(--cc-text)' }, children: title }), subtitle && (_jsx("div", { style: { fontSize: 11.5, color: 'var(--cc-text-2)', marginTop: 1 }, children: subtitle }))] }), _jsx(ChevronRight, { size: 14, style: { color: 'var(--cc-text-3)' } })] }));
}
export default Drawer;
